import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
} from '@mui/material';
import { updateFolder } from '../../services/folders';
import type { Folder } from '../../types';

interface EditFolderDialogProps {
  open: boolean;
  onClose: () => void;
  folder: Folder;
  onUpdated: () => void;
}

export default function EditFolderDialog({ open, onClose, folder, onUpdated }: EditFolderDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(folder.name);
      setDescription(folder.description);
    }
  }, [open, folder]);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await updateFolder(folder.id, { name: name.trim(), description: description.trim() });
      onClose();
      onUpdated();
    } catch (err) {
      console.error('Failed to update folder:', err);
      alert('Failed to update folder. Check console for details.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit Folder</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            autoFocus
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={2}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || !name.trim()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
